import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isPast } from 'date-fns';
import { Sidebar, Select, notify, Modal, SkeletonLoader, TableLoader } from 'app/components';
import { ShareFat, FileMagnifyingGlass, ChartLineUp, UserFocus, CalendarHeart, Info, MoneyWavy, List, CaretLeft, X } from '@phosphor-icons/react';
import { EventDetails, Tickets, BookingDetails, AttendeesTable, Dashboard } from './containers';
import './style';
import { getEventById, updateEvent } from './api';
import { formatTimestampWithStartDate } from '../event-list/util';
import ShareEvent from '../event-buyerflow/components/shareEvent';
import { getBookingRefundInfo } from '../booking/api';
import RefundDashboard from './containers/refundDashboard/refundDashboard';
import { adminPanelOption, userOptions } from '../../components/navbar/data';

const menuItems = [
  { label: 'Dashboard',
    value: 'dashboard', 
    icon: <ChartLineUp size={24} />, 
  },
  { label: 'Attendees', 
    value: 'attendees', 
    icon: <UserFocus size={24} />, 
  },
];

const eventDetailsSections = ['about', 'locale', 'image', 'description', 'note'];

const checkItems = [
  {
    label: 'Event details',
    check: true,
    subItems: [
      { label: 'About', value: 'about' },
      { label: 'When and where?', value: 'locale'},
      { label: 'Image', value: 'image'},
      { label: 'Event Description', value: 'description'},
      { label: 'Terms and Conditions', value: 'note'},
    ],
  },
  { label: 'Tickets', check: true, value: 'tickets'},
  { label: 'Booking details', check: true, value: 'bookingDetails' },
];

const EventDashboard = ({ stateName }) => {
  const [payoutVisible, setPayoutVisible] = useState(true);
  const [activeSection, setActiveSection] = useState('about');
  const [selectedItem, setSelectedItem] = useState('about');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [refundInfo, setRefundInfo] = useState(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const { eventDetails, mongoId } = useSelector((state) => state[stateName || 'eventDashboard'])
  const { userData, activeRequests } = useSelector((state) => state.app);
  const isAdmin = false;

  const profileOptions = useMemo(() => {
    const options = userOptions.buyer || [];
    return userData?.isAdmin ? [...adminPanelOption, ...options] : [...options];
  }, [userData]);
  
  // Loading states
  const isEventLoading = activeRequests?.includes('get-event-by-id');
  const isRefundInfoLoading = activeRequests?.includes('get-booking-refund-info');

  useEffect(() => {
    if (
      eventDetails?.id &&
      (eventDetails?.status === 'CANCELLED' || eventDetails?.is_reschedule === true)
    ) {
      getBookingRefundInfo(eventDetails?.id, (response) => {
        setRefundInfo(response);
      });
    }
    if (params?.eventId) getEventById(params.eventId);
  }, [params?.eventId, eventDetails?.id, eventDetails?.status, eventDetails?.is_reschedule]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!isProfileMenuOpen) return;
      if (menuRef.current && menuButtonRef.current) {
        if (!menuRef.current.contains(e.target) && !menuButtonRef.current.contains(e.target)) {
          setIsProfileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isProfileMenuOpen]);

  useEffect(() => {
    if (eventDetails?.end_date) {
      const isDatePassed = (dateString) => {
        const inputDate = new Date(dateString.replace(" ", "T")); 
        const currentDate = new Date();
        return inputDate < currentDate;
      }

      setPayoutVisible(isDatePassed(eventDetails?.end_date) && eventDetails?.is_active);
    };
  }, [eventDetails?.end_date, eventDetails?.is_active]);

  const handleMenuClick = (item) => {
    setSelectedItem(item); 
    setActiveSection(item.value);
    // Hide sidebar on mobile after menu selection
    if (window.innerWidth <= 768) {
      setIsSidebarVisible(false);
    }

  };

const handleBackClick = () => {
  // On mobile, if sidebar is hidden, show it. If visible, navigate back
  if (window.innerWidth <= 768) {
    if (!isSidebarVisible) {
      setIsSidebarVisible(true);
    } else {
      navigate(-1);
    }
  } else {
    navigate(-1);
  }
};

  const handleMenuToggle = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleProfileMenuClick = (menuId) => {
    switch (menuId) {
    case 'user-profile':
    case 'my-listing':
    case 'events':
    case 'bookings':
    case 'admin/primary':
    case 'admin/events':
      navigate(`/${menuId}`);
      break;
    case 'support':
      window.open('mailto:rescue@takemytickets.co.in');
      break;
    case 'logout': {
      navigate('/');
      notify('success', 'logged out');
      dispatch({ type: 'APP:REMOVE_JWT_TOKEN' });
      break;
    }
    default:
      navigate('/');
    }
    setIsProfileMenuOpen(false);
  };

  const onChange = (data, successMsg, callback) => {
    updateEvent(params.eventId, data, (value) => {
      if (callback) callback(value);
      notify(
        'success', successMsg, 
        { position: 'bottom-center', duration: 2000 },
      );
    });
  };

  const { startTime, endTime } = formatTimestampWithStartDate(eventDetails?.start_date, eventDetails?.end_date);
  const readOnly = eventDetails?.is_active && isPast(new Date(eventDetails?.end_date));

  const headerContent = (
    <div className='event-content flex flex-col gap-2 border-b border-grey-10 pb-6'>
      <div className='heading-24 break-all'>{eventDetails?.name}</div>
      <div className='text-16 flex gap-0.5 text-grey-70'>{startTime} to {endTime}</div>
      {eventDetails?.status === 'APPROVED' && (<div className='flex gap-2 py-1'>
        <Select
          icon={<ShareFat color="#101010" 
            size={20} />}
          label="Share"
          onClick={() => setIsShareModalOpen(true)}
          className='px-6 py-5'
          width={160}
        />
        {/* <Select
          icon={<FileMagnifyingGlass color="#101010" 
            size={20} />}
          label="Preview"
          // onClick={}
          className='px-6 py-5'
          width={160}
        /> */}
      </div>)}
      {eventDetails?.status == "CANCELLED" &&
      <div className="flex items-center gap-3 px-8 py-5 rounded-xl bg-[#FFF4F4] w-full">
        <Info size={20} color="#D92D20" />
        <span className="text-[#D92D20] text-sm font-medium">Event Cancelled</span>
      </div>}
      {eventDetails?.status !== "CANCELLED" && (eventDetails?.is_reschedule == true) &&
      <div className="flex items-center gap-3 px-8 py-5 rounded-xl bg-[#E5FFF6] w-full">
        <Info size={20} color="green" />
        <span className="text-[green] text-sm font-medium">Rescheduled Event</span>
      </div>}
      {eventDetails?.status != "CANCELLED" && payoutVisible && (
        <div className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-green-50 px-6 py-3 text-green-80 text-base font-medium">
        <CalendarHeart size={32} color="#009D64" weight = "fill" />
          Event Finished
         </div>
      )}
    </div>
  );

  // Add Refund Dashboard menu item if event is cancelled or rescheduled
  const menuItemsWithRefund = [
    ...(eventDetails?.status === 'CANCELLED' || eventDetails?.is_reschedule === true
      ? [{
          label: 'Refund Dashboard',
          value: 'RefundDashboard',
          icon: <MoneyWavy size={24} />, // Use a suitable icon
        }]
      : []),
    ...menuItems,
  ];

  const shouldShowLoader = isEventLoading && !eventDetails?.id;

  // Show loading state while event is loading
  if (shouldShowLoader) {
    return (
      <div className='flex h-full mt-[100px] overflow-y-hidden'>
        <div className="w-[330px] p-6">
          <SkeletonLoader type="form" className="space-y-4" />
        </div>
        <div className="flex-1 p-6">
          <SkeletonLoader type="card" count={3} />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Header */}
      <div className="dashboard-mobile-header">
        <button className="back-button" onClick={handleBackClick}>
          <CaretLeft size={24} />
        </button>
        <div className="relative">
          <button
            ref={menuButtonRef}
            className={`menu-button ${isProfileMenuOpen ? 'open' : ''}`}
            onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
          >
            {isProfileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
          {isProfileMenuOpen && (
            <div ref={menuRef} className="menu-wrapper absolute right-0 top-8 z-50">
              {profileOptions.map((option, index) => (
                <div key={option.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    className="menu-item px-5 py-3 flex items-center justify-between"
                    onClick={() => {
                      handleProfileMenuClick(option.id);
                      setIsProfileMenuOpen(false);
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { handleProfileMenuClick(option.id); setIsProfileMenuOpen(false); } }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="menu-icon">{option.icon}</span>
                      <span>{option.label}</span>
                    </div>
                  </div>
                  {index !== profileOptions.length - 1 && (
                    <div className="divider w-full" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className='flex h-full mt-[100px] overflow-y-hidden'>
        <Sidebar
          header={headerContent}
          menuItems={((eventDetails?.status === 'APPROVED' || eventDetails?.status === 'CANCELLED') || eventDetails?.is_reschedule === true ) ? menuItemsWithRefund : []}
          checkItems={checkItems}
          onMenuClick={handleMenuClick}
          selectedItem={selectedItem}
          className={`dashboard-sidebar ${!isSidebarVisible ? 'hidden' : ''}`}
          colorMode="black"
          width="full"
        />

      {activeSection === 'dashboard' && (<Dashboard />)}

      {activeSection === 'attendees' && (<AttendeesTable readOnly={readOnly} />)}

      {eventDetailsSections.includes(activeSection) && 
        <EventDetails activeSection={activeSection}
          formattedDate={`${startTime} - ${endTime}`}
          isAdmin={isAdmin}
          onChange={onChange}
          readOnly={!isAdmin && readOnly && !['description', 'image'].includes(activeSection)} />
      }

      {activeSection === 'tickets' && <Tickets onChange={onChange} readOnly={!isAdmin && readOnly} />}
      
      {activeSection === 'bookingDetails' && <BookingDetails onChange={onChange} readOnly={!isAdmin && readOnly} />}  

      {activeSection === 'RefundDashboard' && (
        <RefundDashboard refundInfo={refundInfo} stateName={stateName || 'eventDashboard'} />
      )}

      <Modal
        isOpen={isShareModalOpen}
        className="dashboard-share-modal"
        hasClose={true}
        onClose={() => setIsShareModalOpen(false)}
        title="Share Event"
        size="regular"
      >
        <ShareEvent link={`${window.location.origin}/event/${mongoId}`} showLink={true} />
      </Modal>
      </div>
    </>
  );
};

export default EventDashboard;
